import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon
} from '@mui/icons-material';
import API_BASE_URL from '../apiConfig';
import { getDataGridStyles } from '../styles/dataGridStyles';
import { useTheme } from '../ThemeContext';

const AdminClubRequestsTable = () => {
  const { darkMode } = useTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  // Get shared DataGrid styles
  const dataGridStyles = getDataGridStyles(darkMode);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/new-club-requests`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch requests');
      }
      
      const data = await response.json();
      setRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setError('Failed to load club requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (params) => {
    setSelectedRequest(params.row);
    setAdminNotes('');
    setModalOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      // First, create the club using the club creation API
      const clubData = {
        name: selectedRequest.club_name,
        handle: selectedRequest.club_handle,
        description: selectedRequest.club_description,
        owner_id: selectedRequest.user_id,
        avatar_url: null // You can set a default avatar or leave null
      };

      const clubResponse = await fetch(`${API_BASE_URL}/api/clubs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(clubData),
      });

      if (!clubResponse.ok) {
        const errorData = await clubResponse.json();
        throw new Error(errorData.error || 'Failed to create club');
      }

      // Then delete the request
      const deleteResponse = await fetch(`${API_BASE_URL}/api/new-club-requests/${selectedRequest.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!deleteResponse.ok) {
        throw new Error('Failed to delete request');
      }

      // Refresh the requests list
      await fetchRequests();
      setModalOpen(false);
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error approving request:', error);
      setError(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/new-club-requests/${selectedRequest.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete request');
      }

      // Refresh the requests list
      await fetchRequests();
      setModalOpen(false);
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error rejecting request:', error);
      setError(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      field: 'club_name',
      headerName: 'Club Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            @{params.row.club_handle}
          </Typography>
        </Box>
      )
    },
    {
      field: 'user_name',
      headerName: 'Requested By',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.value || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.user_email}
          </Typography>
        </Box>
      )
    },
    {
      field: 'club_category',
      headerName: 'Category',
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Submitted',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDate(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: () => (
        <Chip 
          label="Pending" 
          color="warning"
          size="small"
        />
      )
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        New Club Requests ({requests.length})
      </Typography>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={requests}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          onRowClick={handleRowClick}
          sx={{
            ...dataGridStyles,
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
              },
            },
          }}
        />
      </Paper>

      {/* Request Details Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Club Request Details
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedRequest.club_name} (@{selectedRequest.club_handle})
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Category:</strong> {selectedRequest.club_category}
              </Typography>
              
              <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 2 }}>
                {selectedRequest.club_description}
              </Typography>
              
              <Typography variant="body2" gutterBottom>
                <strong>Contact Email:</strong> {selectedRequest.contact_email}
              </Typography>
              
              {selectedRequest.contact_phone && (
                <Typography variant="body2" gutterBottom>
                  <strong>Contact Phone:</strong> {selectedRequest.contact_phone}
                </Typography>
              )}
              
              <Typography variant="body2" gutterBottom>
                <strong>Requested By:</strong> {selectedRequest.user_name} ({selectedRequest.user_email})
              </Typography>
              
              <Typography variant="caption" color="text.secondary">
                Submitted on {formatDate(selectedRequest.created_at)}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes (optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                margin="normal"
                placeholder="Add any notes about this decision..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setModalOpen(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReject}
            variant="outlined"
            color="error"
            startIcon={<RejectIcon />}
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
          <Button 
            onClick={handleApprove}
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Approve & Create Club'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminClubRequestsTable; 